import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
} from '@headlessui/react';
import _ from 'lodash';
import Modal from '@/core/design-system/Modal';
import { ISettingTab, tabs } from './tabs/tabs';
import TabHeader from './tabs/TabHeader';
import { cn } from '@/core/utils/cn';
import { ChevronDownIcon } from '@/assets/icons/chevron-down.icon';
import { useState } from 'react';

type SettingsDialogProps = {
  isOpen: boolean;
  toggleDialog: () => void;
};

const SettingsDialog = ({ isOpen, toggleDialog }: SettingsDialogProps) => {
  const [selectedOption, setSelectedOption] = useState<ISettingTab>(tabs[0]);

  return (
    <Modal isOpen={isOpen} toggleDialog={toggleDialog}>
      <div className="p-3 md:p-6 pb-10 md:pb-20 rounded-2xl bg-dark border border-1 border-secondary">
        <TabGroup vertical className="block md:flex w-full gap-x-10">
          <TabHeader title={selectedOption.title} toggleDialog={toggleDialog} className="mt-2 flex md:hidden" />

          <TabList className="w-full md:w-[40%] mb-5 md:mb-0 p-1 md:p-0 bg-main md:bg-transparent rounded-xl md:rounded-none">
            <div className="block md:hidden">
              <Listbox value={selectedOption} onChange={setSelectedOption}>
                <ListboxButton
                  className={cn(
                    'relative flex items-center justify-center gap-x-1 w-full rounded-lg bg-surface-dark py-1.5 pr-8 pl-3 text-left text-sm/6 text-white',
                    'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2',
                  )}
                >
                  {selectedOption.icon}
                  {selectedOption.name}
                  <ChevronDownIcon
                    className="group pointer-events-none absolute top-2.5 right-2.5 size-4"
                    aria-hidden="true"
                  />
                </ListboxButton>
                <ListboxOptions
                  anchor="bottom"
                  transition
                  className={cn(
                    'w-[var(--button-width)] rounded-lg border border-secondary bg-main p-1 [--anchor-gap:var(--spacing-1)] focus:outline-none',
                    'transition duration-0 data-[leave]:data-[closed]:opacity-0',
                  )}
                >
                  {tabs.map((tab: ISettingTab) => (
                    <ListboxOption
                      key={tab.key}
                      value={tab}
                      className="group flex cursor-default items-center gap-2 rounded-lg py-1.5 px-3 select-none data-[focus]:bg-surface-dark"
                    >
                      {tab.icon}
                      <div className="text-sm/6 text-white">{tab.name}</div>
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Listbox>
            </div>
            <div className="hidden md:block">
              {tabs.map((tab: ISettingTab) => (
                <Tab
                  key={tab.key}
                  className="mb-2 w-full flex items-center justify-center md:justify-start data-[selected]:bg-surface-dark data-[hover]:bg-surface-dark text-lighter rounded-lg p-2 gap-x-2 transition-all duration-200 ease-in-out"
                >
                  {tab.icon}
                  {tab.name}
                </Tab>
              ))}
            </div>
          </TabList>
          <TabPanels className="w-full">
            <div className="hidden md:block">
              {tabs.map((tab: ISettingTab) => (
                <TabPanel key={tab.key}>
                  <TabHeader title={tab.title} toggleDialog={toggleDialog} className="hidden md:flex" />
                  {tab.component}
                </TabPanel>
              ))}
            </div>

            <div className="block md:hidden">{selectedOption.component}</div>
          </TabPanels>
        </TabGroup>
      </div>
    </Modal>
  );
};

export default SettingsDialog;
